import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Plus, FileText, Calendar } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import ExamCreateWizard from './exams/ExamCreateWizard';

const Exams = () => {
  const { user, canManageFeature } = useAuth();
  const canManageExams = canManageFeature('exams');
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => { fetchExams(); }, [user?.school_id]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => fetchExams();
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [user?.school_id]);

  const fetchExams = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      const response = await api.get('/exams', {
        params: {
          ...(schoolId ? { school_id: schoolId } : {}),
          ...(academicYearId ? { academic_year_id: academicYearId } : {}),
        },
      });
      setExams(response.data.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch exams', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const examTypes = [
    { value: 'term', label: 'Term Exam' },
    { value: 'mid_term', label: 'Mid-Term' },
    { value: 'final', label: 'Final Exam' },
    { value: 'unit_test', label: 'Unit Test' },
    { value: 'other', label: 'Other' },
  ];

  const statusColors = {
    scheduled: 'bg-blue-500/20 text-blue-400',
    ongoing: 'bg-amber-500/20 text-amber-400',
    completed: 'bg-green-500/20 text-green-400',
    cancelled: 'bg-red-500/20 text-red-400',
  };

  const datesheetColors = {
    none: 'bg-muted text-muted-foreground',
    draft: 'bg-amber-500/20 text-amber-400',
    published: 'bg-green-500/20 text-green-400',
  };

  return (
    <div className="space-y-6" data-testid="exams-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Examinations</h1>
          <p className="text-muted-foreground">Manage exams, datesheets and results</p>
        </div>
        {canManageExams && (
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setWizardOpen(true)}>
            <Plus className="w-4 h-4 mr-2" /> Create Exam
          </Button>
        )}
      </div>

      <ExamCreateWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onComplete={fetchExams}
      />

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div>
      ) : exams.length === 0 ? (
        <Card className="border-border">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No exams found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {exams.map(exam => (
            <Card
              key={exam.id}
              className="border-border hover:border-blue-500/40 cursor-pointer transition-colors"
              onClick={() => navigate(`/exams/${exam.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex flex-col items-center justify-center">
                      <span className="text-xs text-blue-400">{new Date(exam.start_date).toLocaleDateString('en', { month: 'short' })}</span>
                      <span className="text-lg font-bold text-foreground">{new Date(exam.start_date).getDate()}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground text-lg">{exam.name}</h3>
                      <p className="text-sm text-muted-foreground">{exam.description || examTypes.find(t => t.value === exam.exam_type)?.label}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Calendar className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {new Date(exam.start_date).toLocaleDateString()} - {new Date(exam.end_date).toLocaleDateString()}
                        </span>
                        {exam.datesheet_status && exam.datesheet_status !== 'none' && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${datesheetColors[exam.datesheet_status] || datesheetColors.none}`}>
                            datesheet: {exam.datesheet_status}
                          </span>
                        )}
                        {exam.results_status && exam.results_status !== 'none' && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${datesheetColors[exam.results_status] || datesheetColors.none}`}>
                            results: {exam.results_status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm ${statusColors[exam.status] || statusColors.scheduled}`}>
                    {exam.status}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Exams;
